import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { UrlStateAllowed, UrlStateShape } from './url-state';
import { readUrlState, writeUrlState } from './url-state';

export interface UrlStateUpdateOptions {
  /**
   * true の場合は履歴エントリを積まず現在のエントリを置き換える。
   * 入力途中の検索文字列など、戻る操作の対象にしたくない更新で使う。
   */
  replace?: boolean;
}

export type UrlStateSetter<S extends UrlStateShape> = (
  patch: Partial<S>,
  options?: UrlStateUpdateOptions
) => void;

/**
 * 一覧画面のページ番号・フィルタ・ソートを URL クエリで管理するフック。
 *
 * 更新は React Router の `setSearchParams`（既定で pushState）経由で行うため、
 * ブラウザの戻る / 進むで表示状態が復元され、URL の共有・リロードでも同じ画面になる。
 *
 * @param defaults 既定値。値の型（string / number / boolean）が復元時の型になる
 * @param allowed キーごとの許可値。URL に不正値が入った場合は既定値へフォールバックする
 *
 * @example
 * const [{ page, status }, setUrlState] = useUrlState(
 *   { page: 0, status: '' },
 *   { status: ['', 'ACTIVE', 'SUSPENDED'] }
 * );
 * // フィルタ変更時はページも同時に戻し、履歴を 1 エントリに収める
 * setUrlState({ status: 'ACTIVE', page: 0 });
 */
export function useUrlState<S extends UrlStateShape>(
  defaults: S,
  allowed?: UrlStateAllowed<S>
): readonly [S, UrlStateSetter<S>] {
  const [searchParams, setSearchParams] = useSearchParams();

  // 呼び出し側はオブジェクトリテラルを渡すため、初回の値を固定して再計算を防ぐ
  const [initialDefaults] = useState(defaults);
  const [initialAllowed] = useState(allowed);

  const state = useMemo(
    () => readUrlState(searchParams, initialDefaults, initialAllowed),
    [searchParams, initialDefaults, initialAllowed]
  );

  const setState = useCallback<UrlStateSetter<S>>(
    (patch, options) => {
      setSearchParams(prev => writeUrlState(prev, initialDefaults, patch), {
        replace: options?.replace ?? false
      });
    },
    [setSearchParams, initialDefaults]
  );

  return [state, setState] as const;
}
